import {
	autoUpdate,
	computePosition,
	flip,
	Middleware,
	MiddlewareState,
	offset,
	Placement,
	size,
} from '@floating-ui/dom';
import { Component, Element, Method, Prop, Watch, h, State } from '@stencil/core';
import { HTMLStencilElement, Listen } from '@stencil/core/internal';
import Bind from 'bind-decorator';

import { StyleCollection } from '../../types';
import { generateCssClasses } from '../../utils/generate-css-classes';
import { createMutationObserver } from '../../utils/mutation-observer';
import { isMobile } from '../../utils/responsive';
import { timeoutAsync } from '../../utils/timeout-async';

@Component({
	shadow: true,
	styleUrl: 'zds-popout.scss',
	tag: 'zds-popout',
})
export class ZdsPopout {
	arrowElement?: HTMLDivElement;
	currentPos: string;
	mutationObserver: MutationObserver;
	positionForFirstOpen: boolean;
	popoutCleanup: () => void;
	targetElement?: Element;

	hidePopoutTimeout: ReturnType<typeof setTimeout>;
	showPopoutTimeout: ReturnType<typeof setTimeout>;

	animationOrigins: Record<string, string> = {
		'right-start': '0px 50%',
		right: '0px 50%',
		'right-end': '0px 50%',
		'bottom-end': '50% 0px',
		bottom: '50% 0px',
		'bottom-start': '50% 0px',
		'top-start': '50% 100%',
		top: '50% 100%',
		'top-end': '50% 100%',
		'left-start': '100% 50%',
		left: '100% 50%',
		'left-end': '100% 50%',
	};

	@Element() hostElement: HTMLStencilElement;

	@Prop() animation: 'fade' | 'grow' = 'fade'; // Internal
	@Prop() arrow: boolean = false;
	@Prop() customScrollBar: boolean = false;
	@Prop() dynamicWidth: boolean = false;
	@Prop() fallbackPlacements: string = '';
	@Prop() hoverDelay: number = 120;
	@Prop() maxHeight: number = 500;
	@Prop() maxWidth?: number | string;
	@Prop() minHeight: number; // minimum Height for the content to flip in case it does not fit
	@Prop() offsetMain: number;
	@Prop() offsetCross: number;
	@Prop({ reflect: true }) open: boolean = false;
	@Prop() position: 'stretch' | 'start' | 'center' | 'end' = 'stretch';
	@Prop() placement: 'left' | 'right' | 'top' | 'bottom' = 'bottom';
	@Prop() strategy: 'absolute' | 'fixed' = 'absolute';
	@Prop() target: string | HTMLElement;
	@Prop() touchDelay: number = 700; // deprecated
	@Prop() trigger: 'click' | 'hover' | 'manual' = 'manual';

	/**
	 * this is an internal prop that is used by other wrapper components to tell the popout to always recalculate it's positioning and fallback placements,
	 * initially was added for the zds-tooltip, this will bypass the positionForFirstOpen variable.
	 */
	@Prop() alwaysComputePosition: boolean = false;

	@State() isHiding: boolean = false;
	@State() targetFound: boolean = false;
	@State() visible: boolean = false;

	@Watch('open')
	async onOpenChange(newValue: boolean, oldValue: boolean) {
		if (newValue === oldValue) {
			return;
		}

		if (newValue) {
			await this.show();
		} else {
			await this.hide();
		}
	}

	@Watch('position')
	async onPositionChange() {
		await this.computePositionPopout(this.targetElement);
	}

	@Watch('strategy')
	onStrategyChange(newValue: 'absolute' | 'fixed') {
		this.hostElement.style.position = newValue === 'fixed' ? 'fixed' : null;
		this.updatePosition();
	}

	@Watch('target')
	onTargetChange() {
		this.removeAllEventListeners();

		// Handle new target
		this.attachEvents();
	}

	@Bind
	@Method()
	async hide(): Promise<void> {
		if (!this.open && !this.visible) {
			return;
		}

		this.isHiding = true;

		if (this.showPopoutTimeout) {
			clearTimeout(this.showPopoutTimeout);
		}

		this.hidePopoutTimeout = setTimeout(() => {
			this.isHiding = false;
			(this.popoutCleanup || Function)();
			this.open = false;
			this.visible = false;
		}, 250);
	}

	@Bind
	@Method()
	async show(): Promise<string> {
		if (this.open && this.visible) {
			return;
		}

		if (this.isHiding) {
			clearTimeout(this.hidePopoutTimeout);
			this.isHiding = false;
		}

		if (this.showPopoutTimeout) {
			clearTimeout(this.showPopoutTimeout);
		}

		if (this.hidePopoutTimeout) {
			clearTimeout(this.hidePopoutTimeout);
		}

		this.showPopoutTimeout = setTimeout(() => {
			this.open = true;
			this.updatePosition();

			requestAnimationFrame(() => {
				this.visible = true;
				this.updatePosition();
			});
		}, this.hoverDelay);
	}

	@Bind
	@Method()
	async updatePosition(): Promise<void> {
		this.positionForFirstOpen = true;

		// Make sure to clean up the autoUpdater if there is any, as to not register more than necessary
		(this.popoutCleanup || Function)();
		this.popoutCleanup = () => {
			// Prevent React errors
			if (!this.targetElement || !this.hostElement) return;

			const updateFunc = autoUpdate(
				this.targetElement,
				this.hostElement,
				() => void this.computePositionPopout(this.targetElement),
			);
			updateFunc();
		};
		await this.computePositionPopout(this.targetElement);
	}

	/*
	 * When click outside the target, hide the popout.
	 */
	@Listen('zdsClick', { target: 'document' })
	@Listen('click', { target: 'document' })
	async onDocumentClick(event: CustomEvent) {
		if (this.trigger !== 'click' && !isMobile()) {
			return;
		}

		if (
			!this.hostElement.contains(event.target as HTMLElement) &&
			!this.targetElement.contains(event.target as HTMLElement)
		) {
			await this.hide();
		}
	}

	connectedCallback() {
		this.mutationObserver = createMutationObserver(this.hostElement, this.handleMutation);
	}

	async attachEvents() {
		let numOfTries = 5;

		let elementToBeTargeted: HTMLElement;
		if (typeof this.target === 'string') {
			elementToBeTargeted = document.querySelector(this.target);
			// because of some race conditions the tooltip might render before the actual target,
			// we set a small loop to wait for the target to render
			while (!elementToBeTargeted && numOfTries > 0) {
				elementToBeTargeted = document.querySelector(this.target);
				numOfTries--;
				await timeoutAsync(1000);
			}
		} else if (typeof this.target === 'object') {
			elementToBeTargeted = this.target;
		}

		if (!elementToBeTargeted) {
			console.error(`zds-popout: the element with target '${this.target}' could not be found in the dom`);
			return;
		}

		this.targetElement = elementToBeTargeted;
		this.targetFound = true;

		if (this.trigger === 'hover') {
			this.targetElement.addEventListener('mouseenter', this.onHoverMouseEnter);
			this.targetElement.addEventListener('mouseleave', this.onHoverMouseLeave);
		} else if (this.trigger === 'click') {
			this.targetElement.addEventListener('pointerdown', this.show);
		}
	}

	async componentDidLoad() {
		await this.attachEvents();

		if (this.open) {
			await this.show();
		}

		if (this.strategy === 'fixed') {
			this.hostElement.style.position = 'fixed';
		}
		this.onStrategyChange(this.strategy);

		if (this.maxWidth !== 'none') {
			this.hostElement.style.setProperty('max-width', `${this.maxWidth}px`);
		}

		// Option parameter `true` triggers this event whenever any parent scrolls
		window.addEventListener('scroll', this.onAnyElementScroll, true);
		window.addEventListener('resize', this.onWindowResize);
	}

	componentWillLoad() {
		this.onTargetChange();
	}

	disconnectedCallback() {
		this.mutationObserver?.disconnect();
		this.removeAllEventListeners();
	}

	determineFallbackPlacements(primaryPlacement: ZdsPopout['placement'], placementSuffix: string): Placement[] {
		// allow to override the desire fallback placements
		if (this.fallbackPlacements) {
			return this.fallbackPlacements.split(',').filter(Boolean) as Placement[];
		}

		const sequenceMap = {
			top: ['bottom'],
			bottom: ['top'],
			left: ['right'],
			right: ['left'],
		};

		const otherPlacementsSequence = sequenceMap[primaryPlacement];

		const computeOriginalPlacement = (): [Placement, Placement] => {
			switch (placementSuffix) {
				case 'start': {
					return [`${primaryPlacement}-end`, primaryPlacement];
				}
				case 'end': {
					return [`${primaryPlacement}-start`, primaryPlacement];
				}
				default: {
					return [`${primaryPlacement}-start`, `${primaryPlacement}-end`];
				}
			}
		};

		/**
		 * Generate positions for original placement and all the other possible placements
		 */
		const originalPlacement = computeOriginalPlacement();

		const otherPlacements = otherPlacementsSequence.flatMap((placement: string) => [
			`${placement}${placementSuffix}`,
		]) as Placement[];

		return [...originalPlacement, ...otherPlacements];
	}

	arrowMiddleware(arrowElement: HTMLElement) {
		return {
			name: 'arrowMiddleware',
			async fn(middlewareArguments: MiddlewareState) {
				const { placement } = middlewareArguments;
				const placementDirection = placement.split('-')[0];

				const arrowStylesMapping: { [placement: string]: StyleCollection } = {
					top: {
						left: '50%',
						top: '100%',
						marginTop: '-3px',
						transform: 'translateX(-50%) rotate(90deg)',
					},
					bottom: {
						left: '50%',
						bottom: '100%',
						marginBottom: '-3px',
						transform: 'translateX(-50%) rotate(-90deg)',
					},
					left: {
						top: '50%',
						left: '100%',
						marginLeft: '-1px',
						transform: 'translateY(-50%)',
					},
					right: {
						top: '50%',
						right: '100%',
						marginRight: '-1px',
						transform: 'translateY(-50%) rotate(180deg)',
					},
				};

				if (!arrowStylesMapping[placementDirection]) {
					console.error(`zds-popout: unable to process placement value '${placement}'`);

					return;
				}

				const arrowBaseStyles = { left: null, bottom: null, transform: null, top: null, right: null };
				const arrowStyles = {
					...arrowBaseStyles,
					...arrowStylesMapping[placementDirection],
				};

				Object.assign(arrowElement.style, arrowStyles);

				return {};
			},
		};
	}

	removeAllEventListeners() {
		this.targetElement?.removeEventListener('mouseenter', this.show);
		this.targetElement?.removeEventListener('mouseenter', this.onHoverMouseEnter);
		this.targetElement?.removeEventListener('mouseleave', this.hide);
		this.targetElement?.removeEventListener('mouseleave', this.onHoverMouseLeave);
		this.targetElement?.removeEventListener('pointerdown', this.show);

		window.removeEventListener('scroll', this.onAnyElementScroll, true);
		window.removeEventListener('resize', this.onWindowResize);
	}

	render() {
		const cssClasses = generateCssClasses('zds-popout', {
			[`animation-${this.animation}`]: true,
			dynamicWidth: this.dynamicWidth,
			isHiding: this.isHiding,
			open: this.open,
			visible: this.visible,
		});

		return (
			<div style={{ transformOrigin: this.animationOrigins[this.currentPos] }} class={cssClasses}>
				<div class="zds-popout__wrapper">
					<slot />
				</div>
				{this.renderArrow()}
			</div>
		);
	}

	renderArrow() {
		if (!this.arrow) return;

		return (
			<div class="zds-popout__arrow" ref={(element) => (this.arrowElement = element)}>
				<div class="zds-popout__arrow-wrapper"></div>
			</div>
		);
	}

	async computePositionPopout(target: Element) {
		if (!target) return;

		// Default vertical position is below target; apply horizontal position if needed
		let placementSuffix = '';
		if (this.position === 'start' || this.position === 'end') {
			placementSuffix = '-' + this.position;
		}

		// We cannot use `this` in the middelware, else floating-ui will not understand it
		const self = this; // eslint-disable-line @typescript-eslint/no-this-alias

		// these middleware functions are used to calculate then appropriate x and y positions for the popout
		const middleware: Middleware[] = [
			(self.alwaysComputePosition || self.positionForFirstOpen) &&
				offset({ mainAxis: this.offsetMain ?? 10, crossAxis: this.offsetCross ?? 0 }),

			// reset maxHeight for proper flip calculation
			self.positionForFirstOpen &&
				size({
					apply({ elements, availableHeight }) {
						if (self.minHeight) {
							elements.floating.style.maxHeight = `${Math.min(self.maxHeight, Math.max(availableHeight, self.minHeight))}px`;
						} else {
							elements.floating.style.maxHeight = 'none';
						}
					},
				}),

			// flip to target side that has the most available space within current scroll container
			(self.alwaysComputePosition || self.positionForFirstOpen) &&
				flip({
					fallbackPlacements: self.determineFallbackPlacements(self.placement, placementSuffix),
				}),

			// set the maxHeight of the popout to available height after flip
			(self.alwaysComputePosition || self.positionForFirstOpen) &&
				size({
					apply({ availableHeight, elements }) {
						elements.floating.style.maxHeight = `${Math.min(self.maxHeight, availableHeight)}px`;
					},
				}),

			// set dimensions according to reference (target) element
			(self.alwaysComputePosition || self.positionForFirstOpen) &&
				size({
					apply({ availableHeight, elements }) {
						// width should be re-calculated continuously, while the window possibly scrolls and resizes
						if (self.position === 'stretch') {
							elements.floating.style.width = `${(elements.reference as HTMLElement).clientWidth}px`;
						} else {
							elements.floating.style.width = 'auto';
						}

						// available height should only be calculated initially, when the popover is in its opening stage
						self.positionForFirstOpen &&
							(elements.floating.style.maxHeight = `${Math.min(self.maxHeight, availableHeight) - 10}px`);
					},
				}),

			self.arrow && self.arrowMiddleware(self.arrowElement),
		].filter(Boolean) as Middleware[];

		return await computePosition(target, this.hostElement, {
			placement: `${self.placement}${placementSuffix}` as Placement,
			middleware,
			strategy: self.strategy,
		}).then(({ x, y, placement }) => {
			this.currentPos = placement;
			// y position in reference to target element should only be set once, when the popover is in its opening stage
			if (this.alwaysComputePosition || this.positionForFirstOpen) {
				this.hostElement.style.top = `${y}px`;

				this.positionForFirstOpen = false;
			}

			// x position should be re-calculated continuously, while the window possibly scrolls and resizes
			this.hostElement.style.left = `${x}px`;
		});
	}

	@Bind
	handleMutation() {
		void this.updatePosition();
	}

	@Bind
	onAnyElementScroll() {
		if (!this.open || this.strategy !== 'fixed') return;

		requestAnimationFrame(() => {
			this.updatePosition();
		});
	}

	@Bind
	onHoverMouseEnter() {
		this.show();
	}

	@Bind
	onHoverMouseLeave() {
		if (this.showPopoutTimeout) {
			clearTimeout(this.showPopoutTimeout);
		}

		this.hide();
	}

	@Bind
	onWindowResize() {
		this.hide();
	}
}
